<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { mockProducts } from './story/fixtures.js';
import PurchaseProjectStatusBadge from './PurchaseProjectStatusBadge.vue';

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['open']);

const { t, locale } = useI18n();

const estimatedTotal = computed(() => {
  return (props.project.items ?? []).reduce((sum, item) => {
    const product = mockProducts.find(p => p.id === item.productId);
    return sum + (product?.price ?? 0) * item.quantity;
  }, 0);
});

const formattedTotal = computed(() =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
    estimatedTotal.value
  )
);

const productCount = computed(
  () => props.project.items?.length ?? props.project.itemsCount ?? 0
);

const createdAt = computed(() =>
  props.project.createdAt
    ? dynamicTime(props.project.createdAt, locale.value)
    : ''
);

const lastSharedAt = computed(() =>
  props.project.lastSharedAt
    ? dynamicTime(props.project.lastSharedAt, locale.value)
    : null
);

const channelIcon = computed(() => {
  const icons = {
    whatsapp: 'i-lucide-message-circle',
    sms: 'i-lucide-smartphone',
    email: 'i-lucide-mail',
  };
  return icons[props.project.lastSharedChannel] ?? 'i-lucide-send';
});
</script>

<template>
  <button
    type="button"
    class="flex flex-col w-full gap-1.5 text-left transition-colors group"
    :class="
      compact
        ? 'rounded-none px-4 py-2.5 hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3'
        : 'px-4 py-3 rounded-xl hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3'
    "
    @click="emit('open', project)"
  >
    <div class="flex items-start justify-between w-full gap-2">
      <span
        class="text-sm font-medium text-n-slate-12 min-w-0 truncate leading-snug"
      >
        {{ project.title }}
      </span>
      <PurchaseProjectStatusBadge :status="project.status" class="shrink-0" />
    </div>

    <div class="flex items-center gap-1.5 flex-wrap text-xs text-n-slate-11">
      <span>{{
        t('PURCHASE_PROJECTS.CARD.CREATED_BY', { name: project.sellerName })
      }}</span>
      <span class="size-1 rounded-full bg-n-slate-8 shrink-0" />
      <span>
        {{ productCount }}
        {{ productCount === 1 ? 'produit' : 'produits' }}
      </span>
      <span class="size-1 rounded-full bg-n-slate-8 shrink-0" />
      <span>{{ formattedTotal }}</span>
    </div>

    <div class="flex items-center justify-between w-full">
      <span class="text-xs text-n-slate-10">{{ createdAt }}</span>
      <div class="flex items-center gap-2">
        <span
          v-if="lastSharedAt && !compact"
          class="flex items-center gap-1 text-xs text-n-slate-10"
        >
          <span :class="channelIcon" class="size-3 shrink-0" />
          {{ t('PURCHASE_PROJECTS.CARD.LAST_SHARED', { date: lastSharedAt }) }}
        </span>
      </div>
    </div>
  </button>
</template>
